import { Widget } from 'models/WidgetModel';
import { normalizeGuardRailsConfig } from './normalizeGuardRailsConfig';
import {
  CPR_AGGREGATION_TYPES,
  CPR_CHART_TYPE,
  CPR_TREND_PERIOD_TYPES,
} from 'components/DiyWidgetEngine/Subtypes/CrossProgramRollup/constants';

/**
 * Free-form maps whose keys are data, not property names: the map's own key is camelCased
 * but its contents are kept as stored. Mirrors Torogoz normaliseCustomWidgetConfig.
 */
const DATA_MAP_KEYS = new Set([
  'metadata',
  'operatoroptions',
  'operatoroption',
  'defaults',
  'styles',
]);

/** Inside query collections `metadata` is a typed record (e.g. `rowType`), not a free-form map. */
const QUERY_COLLECTIONS_KEY = 'querycollections';

const camelCase = (key: string): string =>
  key.length > 0 ? key[0].toLowerCase() + key.slice(1) : key;

const isObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Recursively camelCases property keys. A camelCase key wins over a PascalCase duplicate;
 * data-map contents are left untouched.
 */
function camelCaseKeys(value: any, inQueryCollections: boolean): any {
  if (Array.isArray(value)) {
    return value.map((item) => camelCaseKeys(item, inQueryCollections));
  }
  if (!isObject(value)) {
    return value;
  }

  const entries = Object.entries(value).sort(
    ([a], [b]) => Number(a === camelCase(a)) - Number(b === camelCase(b))
  );

  return Object.fromEntries(
    entries.map(([key, child]) => {
      const lower = key.toLowerCase();
      const isDataMap = DATA_MAP_KEYS.has(lower) && !(inQueryCollections && lower === 'metadata');
      return [
        camelCase(key),
        isDataMap
          ? child
          : camelCaseKeys(child, inQueryCollections || lower === QUERY_COLLECTIONS_KEY),
      ];
    })
  );
}

// Some legacy widgets store trend nested under a stray customWidgetConfig wrapper per collection.
function flattenLegacyTrend(queryCollection: Record<string, any>): Record<string, any> {
  if (!('customWidgetConfig' in queryCollection)) return queryCollection;

  const { customWidgetConfig: wrapper, ...rest } = queryCollection;
  const legacyTrend = wrapper?.trend;
  if (rest.trend || !legacyTrend) return rest;

  return { ...rest, trend: legacyTrend };
}

/**
 * Canonicalizes a widget's customWidgetConfig (key casing + legacy nested trend) so every
 * consumer can rely on a single camelCase shape regardless of how the API saved it.
 */
export function normalizeWidget(widget: Widget): Widget {
  if (isObject(widget?.payload)) {
    const payload = { ...widget.payload };
    // Legacy render/drawer consumers use these PascalCase names. Prefer new wire keys
    // without recursively rewriting data dictionaries inside the payload.
    for (const name of [
      'QueriesMetadata',
      'DataSetGuid',
      'ControllerName',
      'Query',
      'GridFilters',
    ]) {
      const camelName = camelCase(name);
      if (Object.prototype.hasOwnProperty.call(payload, camelName)) {
        payload[name] = payload[camelName];
        delete payload[camelName];
      }
    }
    if (Array.isArray(payload.filters)) {
      payload.Filters = payload.filters;
      delete payload.filters;
    }
    widget = { ...widget, payload };
  }
  if (!widget?.customWidgetConfig) return widget;

  const normalizedConfig = normalizeGuardRailsConfig(
    camelCaseKeys(widget.customWidgetConfig, false)
  );
  if (String(normalizedConfig.chart).toLowerCase() === CPR_CHART_TYPE.toLowerCase()) {
    normalizedConfig.chart = CPR_CHART_TYPE;
    normalizedConfig.aggregationType =
      CPR_AGGREGATION_TYPES.find(
        (type) => type.toLowerCase() === String(normalizedConfig.aggregationType).toLowerCase()
      ) ?? normalizedConfig.aggregationType;
    if (Array.isArray(normalizedConfig.trend)) {
      normalizedConfig.trend = normalizedConfig.trend.map((trend: unknown) =>
        isObject(trend)
          ? {
              ...trend,
              timePeriodType:
                CPR_TREND_PERIOD_TYPES.find(
                  (type) => type.toLowerCase() === String(trend.timePeriodType).toLowerCase()
                ) ?? trend.timePeriodType,
            }
          : trend
      );
    }
  }
  if (Array.isArray(normalizedConfig.queryCollections)) {
    normalizedConfig.queryCollections = normalizedConfig.queryCollections.map(flattenLegacyTrend);
  }

  return { ...widget, customWidgetConfig: normalizedConfig };
}

/**
 * Canonical DIY Engine V2 detection (Enterprise, CPR, etc.), reading the guaranteed camelCase shape
 * produced by normalizeWidget.
 */
export function isDiyWidgetEngineConfig(config: any): boolean {
  // WHY: CPR collections are thin peers (no trend/fields); its widget-level `chart` identifies it.
  // The render resolver still gates it behind `useDiyCrossProgramRollup`.
  if (config?.chart === CPR_CHART_TYPE) return true;
  const collections = config?.queryCollections ?? [];
  return collections.some(
    (qc: any) =>
      qc?.dataCollectionGuid && (qc?.trend || qc?.customWidgetConfig?.trend) && qc?.fields
  );
}
